import { estacaoImprime } from "@/lib/printStation";
import { useState, useEffect, useMemo, useRef } from "react";
import { setStoreTimezone } from "@/lib/store-timezone";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import type { Product, Category, Customer } from "@workspace/api-client-react";
import { formatCurrency, orderDisplayCode } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useThermalPrinter } from "@/hooks/use-thermal-printer";
import { orderToReceipt, formatReceiptBytes } from "@/lib/receipt-formatter";
import {
  ProductCard, CartItemRow, CustomerSection, DeliverySection,
  DiscountSection, PaymentSection, ProductDetailDialog, SuccessDialog,
  SuggestiveSection, MinOrderBanner, MesaSection, EncomendaDoCarrinhoPDV } from "@/components/pdv/PDVShared";
import { itensDeEncomenda } from "@/lib/encomendaUnica";
import { usePdvCart } from "@/hooks/use-pdv-cart";
import {
  Search, ShoppingCart, X, Package, CheckCircle2,
  Calculator, Loader2, Store as StoreIcon, AlertTriangle,
} from "lucide-react";
import { sentenceCasePtBR } from "@/lib/textNormalize";

type StoreInfo = {
  id: string;
  name: string;
  slug: string;
  acceptedPayments?: string[];
  cardSurchargeConfig?: any;
  allowsDelivery?: boolean;
  pdvSettings?: any;
};

export default function PublicPDV() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug || "";
  const { toast } = useToast();
  const printer = useThermalPrinter();

  const storeQuery = useQuery({
    queryKey: ["pdv-public-store", slug],
    queryFn: () => customFetch<StoreInfo>(`/api/pdv/${slug}`, { method: "GET" }),
    enabled: !!slug,
    retry: false,
  });

  const productsQuery = useQuery({
    queryKey: ["pdv-public-products", slug],
    queryFn: () => customFetch<Product[]>(`/api/pdv/${slug}/products`, { method: "GET" }),
    enabled: !!slug && !!storeQuery.data,
  });

  const categoriesQuery = useQuery({
    queryKey: ["pdv-public-categories", slug],
    queryFn: () => customFetch<Category[]>(`/api/pdv/${slug}/categories`, { method: "GET" }),
    enabled: !!slug && !!storeQuery.data,
  });

  const store = storeQuery.data;
  const products = productsQuery.data || [];
  const categories = categoriesQuery.data || [];

  // Relógio da loja pros helpers de data/promoção.
  useEffect(() => {
    setStoreTimezone(((store as any)?.timezone as string | undefined) ?? null);
  }, [store]);

  const printToken = useMemo(() => {
    if (typeof window === "undefined") return "";
    const sp = new URLSearchParams(window.location.search);
    return sp.get("print") || sp.get("token") || "";
  }, []);

  const sinceRef = useRef<Date>(new Date());
  const handledOrderIdsRef = useRef<Set<string>>(new Set());

  const recentOrdersQuery = useQuery<any[]>({
    queryKey: ["pdv-public-recent-orders", slug, printToken],
    queryFn: () => {
      const sinceIso = sinceRef.current.toISOString();
      return customFetch<any[]>(
        `/api/pdv/${slug}/orders/recent?token=${encodeURIComponent(printToken)}&since=${encodeURIComponent(sinceIso)}&limit=50`,
        { method: "GET" }
      );
    },
    enabled: !!slug && !!printToken && !!store,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: false,
  });

  const recentOrders = recentOrdersQuery.data || [];

  useEffect(() => {
    if (!printToken) return;
    if (!store) return;
    if (recentOrders.length === 0) return;

    // Avança o cursor para o pedido mais recente retornado mesmo se a
    // impressora estiver desconectada — o objetivo do cursor é limitar a
    // janela de busca no backend, não controlar deduplicação. A
    // deduplicação real é feita por handledOrderIdsRef + printedOrderIds.
    const since = sinceRef.current;
    const maxDate = recentOrders.reduce((m: Date, o: any) => {
      const d = new Date(o.createdAt);
      return d > m ? d : m;
    }, since);
    if (maxDate > since) sinceRef.current = maxDate;

    if (!printer.prefs.autoPrintNewOrder) return;
    if (!printer.connected) return;

    const storeName = (store as any).name || "Minha Loja";
    const pw = printer.prefs.paperWidth;

    for (const order of recentOrders) {
      if (handledOrderIdsRef.current.has(order.id)) continue;
      if (!estacaoImprime(order)) {
        handledOrderIdsRef.current.add(order.id);
        continue;
      }
      if (printer.printedOrderIds.has(order.id)) {
        handledOrderIdsRef.current.add(order.id);
        continue;
      }
      handledOrderIdsRef.current.add(order.id);
      const shortId = orderDisplayCode(order as any, (store as any)?.orderCodeStyle);
      const receipt = orderToReceipt(order, {
        hideDailyNumber: (store as any)?.dailyOrderNumbering === false,
        timezone: ((store as any)?.timezone as string | undefined) ?? null,
        hideGroupTitles: (store as any)?.showGroupTitles === false,
        orderCodeStyle: (store as any)?.orderCodeStyle ?? null,
        topMargin: (store as any)?.receiptTopMargin ?? null,
        showPaidCheckbox: (store as any)?.receiptPaidCheckbox === true,
        receiptFont: (store as any)?.pdvSettings?.receiptFont ?? null,
        footerMessage: (store as any)?.receiptFooterPrinted ?? null,
        porCliente: (store as any)?.tableQrPerClient === true,
      });
      const data = formatReceiptBytes(storeName, receipt, pw, printer.isAsciiOnlyPrinter);
      printer.printData(data, order.id, "new_order").then((result) => {
        if (result === "printed") {
          toast({ title: `Pedido #${shortId} impresso` });
        } else {
          // No PDV público não temos a fila admin (/api/print-queue exige
          // login). Se printData não confirmou impressão, libera o id pra
          // tentar de novo no próximo ciclo de polling.
          handledOrderIdsRef.current.delete(order.id);
        }
      });
    }
  }, [recentOrders, printToken, printer.prefs.autoPrintNewOrder, printer.connected, store]);

  useEffect(() => {
    if (store?.name) {
      document.title = `PDV - ${store.name}`;
    }
  }, [store?.name]);

  const cart = usePdvCart({
    store,
    products: products as Product[],
    slug,
    ordersEndpoint: `/api/pdv/${slug}/orders`,
    source: "pdv",
    // O servidor só manda o código de quem pode usar (sem a feature vem null).
    matchBarcode: true,
  });

  const useCustomerSearch = (term: string, enabled: boolean) => {
    const { data } = useQuery({
      queryKey: ["pdv-public-customers", slug, term],
      queryFn: () => customFetch<Customer[]>(
        `/api/pdv/${slug}/customers?search=${encodeURIComponent(term)}`,
        { method: "GET" },
      ),
      enabled: enabled && !!slug,
    });
    return { customers: (data as Customer[]) || [] };
  };

  if (storeQuery.isLoading) {
    return (
      <div className="h-[100svh] w-full flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (storeQuery.isError || !store) {
    return (
      <div className="h-[100svh] w-full flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive" />
          <h1 className="text-xl font-bold">PDV indisponível</h1>
          <p className="text-sm text-muted-foreground">
            A loja informada não foi encontrada ou está inativa. Verifique o link com o responsável.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100svh] bg-background overflow-hidden">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <StoreIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-bold truncate">{store.name}</div>
            <div className="text-[11px] text-muted-foreground">PDV — Ponto de Venda</div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <div className={`flex-1 flex flex-col overflow-hidden ${cart.showCartMobile ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-border space-y-3">
            <div className="flex items-center gap-3">
              <Calculator className="w-6 h-6 text-primary" />
              <div>
                <h1 className="text-xl font-bold">Nova venda</h1>
                <p className="text-xs text-muted-foreground">Venda presencial no balcão</p>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={cart.search}
                onChange={(e) => cart.setSearch(e.target.value)}
                placeholder="Buscar por nome ou código"
                className="pl-9"
                data-testid="pdv-search-input"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => cart.setActiveCategory(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cart.activeCategory === null ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
                data-testid="pdv-category-all"
              >
                Todos
              </button>
              {(categories as Category[]).map((c) => (
                <button
                  key={c.id}
                  onClick={() => cart.setActiveCategory(c.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cart.activeCategory === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
                  data-testid={`pdv-category-${c.id}`}
                >
                  {sentenceCasePtBR(c.name)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {productsQuery.isLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : cart.filteredProducts.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum produto encontrado</p>
              </div>
            ) : (
              cart.filteredProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onClick={() => { cart.setDetailProduct(p); cart.setEditingItem(null); }}
                  qtyInCart={cart.getCartItemQty(p.id)}
                  onQuickAdd={() => cart.handleQuickAdd(p)}
                  onQuickRemove={() => cart.handleQuickRemove(p)}
                  onQuickIncrement={() => cart.handleQuickIncrement(p)}
                />
              ))
            )}
          </div>
        </div>

        <div className={`md:w-96 lg:w-[28rem] border-l border-border flex flex-col bg-card overflow-hidden ${cart.showCartMobile ? "flex flex-1" : "hidden md:flex"}`}>
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              <span className="font-semibold">Pedido ({cart.cart.length})</span>
            </div>
            <div className="flex gap-2">
              <button onClick={cart.handleClearCart} className="text-xs text-muted-foreground hover:text-destructive" disabled={cart.cart.length === 0} data-testid="pdv-clear-cart">
                Limpar
              </button>
              <button onClick={() => cart.setShowCartMobile(false)} className="md:hidden p-1 hover:bg-muted rounded" data-testid="pdv-close-cart-mobile">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cart.cart.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">
                Adicione produtos para começar
              </div>
            ) : (
              cart.cart.map((it) => (
                <CartItemRow
                  key={it.cartItemId}
                  item={it}
                  onRemove={() => cart.handleRemoveItem(it.cartItemId)}
                  onQtyChange={(q) => cart.handleQtyChange(it.cartItemId, q)}
                  onEdit={() => { cart.setDetailProduct(it.product); cart.setEditingItem(it); }}
                />
              ))
            )}

            {cart.cart.length > 0 && (
              <>
                {cart.dataUnica && cart.cart.some((it) => (it.product as any)?.isOrderBased) && (
                  <EncomendaDoCarrinhoPDV
                    slug={slug}
                    itens={itensDeEncomenda(cart.cart)}
                    data={cart.encData}
                    hora={cart.encHora}
                    turno={cart.encTurno}
                    horarios={cart.horariosDosTurnos}
                    onChange={cart.setEncomenda}
                  />
                )}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs">Observações do pedido</Label>
                  <textarea
                    value={cart.orderNotes}
                    onChange={(e) => cart.setOrderNotes(e.target.value)}
                    rows={2}
                    placeholder="Ex: caprichar no embrulho..."
                    className="w-full text-sm px-3 py-2 border border-border rounded bg-background"
                    data-testid="pdv-order-notes"
                  />
                </div>

                <MesaSection
                  tableNumber={cart.tableNumber}
                  onTableNumberChange={cart.setTableNumber}
                  payLater={cart.payLater}
                  onPayLaterChange={cart.setPayLater}
                />

                <CustomerSection
                  customer={cart.customer}
                  onChange={cart.setCustomer}
                  customerRequired={cart.customerRequired}
                  useCustomerSearch={useCustomerSearch}
                />

                {cart.allowsDelivery && (
                  <DeliverySection
                    deliveryMode={cart.deliveryMode}
                    onDeliveryModeChange={cart.setDeliveryMode}
                    address={cart.address}
                    onAddressChange={cart.setAddress}
                    store={store}
                    feeInfo={cart.feeInfo}
                    finalDeliveryFee={cart.deliveryFee}
                    freeDeliveryApplied={cart.freeDeliveryApplied}
                    allowsPickup={true}
                    allowsDelivery={true}
                  />
                )}

                <DiscountSection
                  discountValue={cart.discountValue}
                  discountType={cart.discountType}
                  onValueChange={cart.setDiscountValue}
                  onTypeChange={cart.setDiscountType}
                />

                {cart.payLater ? (
                  <div className="text-xs bg-muted/60 border border-border rounded px-3 py-2 text-muted-foreground">
                    Pagamento <span className="font-semibold">Pendente</span> — a forma será escolhida ao fechar a conta (no PDV do painel).
                  </div>
                ) : (
                  <PaymentSection
                    acceptedPayments={cart.acceptedPayments}
                    paymentMethod={cart.paymentMethod}
                    onPaymentMethodChange={cart.setPaymentMethod}
                    paymentMixed={cart.paymentMixed}
                    onPaymentMixedChange={cart.setPaymentMixed}
                    paymentParts={cart.paymentParts}
                    onPaymentPartsChange={cart.setPaymentParts}
                    cashReceived={cart.cashReceived}
                    onCashReceivedChange={cart.setCashReceived}
                    total={cart.total}
                    cashChange={cart.cashChange}
                    crediarioNames={cart.crediarioNames}
                    crediarioInstallments={cart.crediarioInstallments}
                    onCrediarioInstallmentsChange={cart.setCrediarioInstallments}
                    crediarioFirstDue={cart.crediarioFirstDue}
                    onCrediarioFirstDueChange={cart.setCrediarioFirstDue}
                    crediarioInterest={cart.crediarioInterest}
                  />
                )}

                <SuggestiveSection
                  products={products as Product[]}
                  cart={cart.cart}
                  store={store}
                  onPick={cart.handleQuickAdd}
                />
              </>
            )}
          </div>

          {cart.cart.length > 0 && (
            <div className="border-t border-border p-4 space-y-2 bg-card">
              <MinOrderBanner minOrderValue={cart.minOrderValue} subtotal={cart.subtotal} />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(cart.subtotal)}</span>
              </div>
              {cart.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span>
                  <span>- {formatCurrency(cart.discountAmount)}</span>
                </div>
              )}
              {cart.surcharge > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>Acréscimo cartão</span>
                  <span>+ {formatCurrency(cart.surcharge)}</span>
                </div>
              )}
              {cart.deliveryMode === "entrega" && cart.deliveryFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Entrega</span>
                  <span>+ {formatCurrency(cart.deliveryFee)}</span>
                </div>
              )}
              {cart.deliveryMode === "entrega" && cart.freeDeliveryApplied && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Entrega</span>
                  <span>Grátis</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                <span>Total</span>
                <span data-testid="pdv-total">{formatCurrency(cart.total)}</span>
              </div>
              {cart.cashChange > 0 && (
                <div className="flex justify-between text-sm font-semibold text-blue-600">
                  <span>Troco</span>
                  <span>{formatCurrency(cart.cashChange)}</span>
                </div>
              )}
              <Button
                onClick={cart.handleFinalize}
                disabled={cart.submitting || (cart.minOrderValue > 0 && cart.subtotal < cart.minOrderValue) || (cart.deliveryMode === "entrega" && (cart.feeInfo.noDeliveryBlocked || cart.feeInfo.feeNotResolved))}
                className="w-full mt-2"
                size="lg"
                data-testid="pdv-finalize-button"
              >
                {cart.submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registrando...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Finalizar pedido</>}
              </Button>
            </div>
          )}
        </div>
      </div>

      {cart.cart.length > 0 && !cart.showCartMobile && (
        <button
          onClick={() => cart.setShowCartMobile(true)}
          className="md:hidden fixed bottom-6 right-6 bg-primary text-primary-foreground rounded-full shadow-lg px-5 py-3 flex items-center gap-2 font-semibold z-30"
          data-testid="pdv-cart-fab"
        >
          <ShoppingCart className="w-5 h-5" />
          {cart.cart.length} • {formatCurrency(cart.total)}
        </button>
      )}

      {cart.detailProduct && (
        <ProductDetailDialog
          product={cart.detailProduct}
          editingItem={cart.editingItem}
          onClose={() => { cart.setDetailProduct(null); cart.setEditingItem(null); }}
          onConfirm={cart.handleAddOrUpdateItem}
          remainingKg={cart.remainingKg(cart.detailProduct, cart.editingItem?.cartItemId)}
          slug={slug}
          dataUnica={cart.dataUnica}
          horarios={cart.horariosDosTurnos}
        />
      )}

      {cart.success && (
        <SuccessDialog
          success={cart.success}
          onPrint={cart.handlePrint}
          onWhatsApp={cart.handleSendWhatsApp}
          onNewSale={cart.handleNewSale}
          printerConnected={printer.connected}
        />
      )}
    </div>
  );
}
